import { gql, useQuery } from '@apollo/client';
import { Box } from '@mui/material';
import { useState } from 'react';
import { SmartReportComponentTypesEnum, SmartReportTypeEnum } from '@/admins/SmartReports/types';
import type { Asset } from '@/types/GraphqlSchema';
import { ComponentTable } from '../Common/ComponentTable';
import { ComponentTableBody } from '../Common/ComponentTableBody';
import { ComponentTableHeader } from '../Common/ComponentTableHeader';
import { LoadingErrorWrapper } from '../Common/LoadingErrorWrapper';
import type {
  ComponentDefinition,
  ComponentEditProps,
  ComponentViewProps,
  UseDataType,
} from '../types';

export const COMPONENT_ID = 'asset_features_table';

const ASSET_FEATURES_QUERY = gql`
  query assetV2($input: EntityIdentifierInput!) {
    assetV2(input: $input) {
      id
      name
      shoreStatus {
        name
      }
      basin {
        name
      }
      formattedWaterDepthM
      formattedReservoirDepthM
      developmentType {
        name
      }
    }
  }
`;

interface AssetFeaturesData {
  blockName: string;
  shoreStatus: string;
  basin: string;
  waterDepth: string;
  reservoirDepth: string;
  assetType: string;
}

const formatAssetFeatures = (asset: Asset): AssetFeaturesData => {
  return {
    blockName: asset.name || 'N/A',
    shoreStatus: asset.shoreStatus?.name || 'N/A',
    basin: asset.basin?.name || 'N/A',
    waterDepth: asset.formattedWaterDepthM || 'N/A',
    reservoirDepth: asset.formattedReservoirDepthM || 'N/A',
    assetType: asset.developmentType?.name || 'N/A',
  };
};

const useData = ({ portfolio, persistedData, onComponentLoad }: UseDataType<AssetFeaturesData>) => {
  const [dataFetched, setDataFetched] = useState(persistedData);

  const assetId = portfolio?.assets?.[0];
  const { loading, error } = useQuery<{ assetV2: Asset }>(ASSET_FEATURES_QUERY, {
    variables: { input: { id: assetId } },
    skip: !!persistedData || !assetId,
    onCompleted: response => {
      if (response?.assetV2) {
        const data = formatAssetFeatures(response.assetV2);
        setDataFetched(data);
        if (onComponentLoad) {
          onComponentLoad({
            componentId: COMPONENT_ID,
            data,
          });
        }
      }
    },
  });

  return { data: dataFetched, error, loading };
};

const ComponentView = ({ data }: ComponentViewProps<AssetFeaturesData>) => {
  if (!data) return null;

  const rows = [
    { label: 'Block Name', value: data.blockName },
    { label: 'Shore Status', value: data.shoreStatus },
    { label: 'Basin', value: data.basin },
    { label: 'Water Depth (m)', value: data.waterDepth },
    { label: 'Reservoir Depth (m)', value: data.reservoirDepth },
    { label: 'Asset Type', value: data.assetType },
  ];

  return (
    <Box
      display='flex'
      flexDirection='column'
      width='100%'
      height='100%'
      maxWidth='100%'
      maxHeight='100%'
      overflow='auto'
    >
      <ComponentTable>
        <ComponentTableHeader>Asset Features</ComponentTableHeader>
        <ComponentTableBody rows={rows} />
      </ComponentTable>
    </Box>
  );
};

const ComponentEdit = ({
  portfolio,
  persistedData,
  onComponentLoad,
}: ComponentEditProps<AssetFeaturesData>) => {
  const { data, loading, error } = useData({
    portfolio,
    persistedData,
    onComponentLoad,
  });

  return (
    <LoadingErrorWrapper loading={loading} error={error} data={data}>
      {data && <ComponentView data={data} />}
    </LoadingErrorWrapper>
  );
};

export const AssetFeaturesTableComponent: ComponentDefinition<AssetFeaturesData> = {
  id: COMPONENT_ID,
  name: 'Asset Features',
  type: SmartReportComponentTypesEnum.Table,
  reportTypes: [SmartReportTypeEnum.Asset],
  Component: ComponentEdit,
  ComponentView,
};
